import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import Navbar from '../../components/Navbar/Navbar';
import PageTitle from '../../components/pagetitle/PageTitle';
import Footer from '../../components/footer/Footer';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Logo from '../../images/logo3.png';
import ServiceBanner from '../../images/servicesBanner.jpg';

const PRODUCTION_API_BASE_URL = 'https://v3api.kumuduhospital.com/api/';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || PRODUCTION_API_BASE_URL).replace(
    /\/?$/,
    '/',
);

const formatFetchError = (error, url) => {
    const msg = String(error?.message || error || '');
    if (msg.includes('CERT_AUTHORITY_INVALID') || msg.includes('certificate')) {
        return (
            `Cannot reach API (invalid HTTPS certificate): ${url}. ` +
            `For direct calls use https:// with the same host as Swagger (e.g. https://localhost:44326/api/), ` +
            `or use REACT_APP_API_BASE_URL=/api/ with src/setupProxy.js for npm start, ` +
            `or trust your dev certificate in Windows / browser, or use production: ${PRODUCTION_API_BASE_URL}`
        );
    }
    if (msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('CONNECTION_RESET')) {
        const httpsHint =
            url.startsWith('http://localhost:44326') || url.startsWith('http://127.0.0.1:44326')
                ? ' If Swagger uses https on this port, stop using http:// here (use /api/ + setupProxy or https://).'
                : '';
        return (
            `Cannot reach API: ${url}. Start the backend, confirm its URL/port, and ensure CORS allows this origin ` +
            `(or use npm start with REACT_APP_API_BASE_URL=/api/ and src/setupProxy.js).${httpsHint}`
        );
    }
    return msg;
};


// Reads use Web API: /api/Channeling/… . Booking is often MVC at site root: /Channeling/AddNewChannelAsync (no /api/).
// REACT_APP_UI_BASE_URL — full booking site origin (e.g. https://www.example.com) when booking is not on the API host.
// REACT_APP_BOOKING_BASE_URL — optional path prefix before BOOKING_PATH (e.g. /api to force …/api/Channeling/… if Swagger says so).
const BOOKING_PATH = (process.env.REACT_APP_BOOKING_PATH || 'Channeling/AddNewChannelAsync').replace(
    /^\//,
    '',
);

const resolveBookingUrlPrefix = () => {
    if (process.env.REACT_APP_UI_BASE_URL) {
        return process.env.REACT_APP_UI_BASE_URL.replace(/\/?$/, '');
    }
    if (process.env.REACT_APP_BOOKING_BASE_URL) {
        return process.env.REACT_APP_BOOKING_BASE_URL.replace(/\/?$/, '');
    }
    const api = API_BASE_URL;
    if (api.startsWith('http://') || api.startsWith('https://')) {
        try {
            const u = new URL(api);
            const stripped = u.pathname.replace(/\/?api\/?$/i, '/');
            const joined = `${u.origin}${stripped === '//' ? '/' : stripped}`.replace(/\/+$/, '');
            return joined;
        } catch {
            return '';
        }
    }
    return '';
};

const BOOKING_URL_PREFIX = resolveBookingUrlPrefix();

const formatDateInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatDisplayDate = (yyyyMmDd) => {
    if (!yyyyMmDd) return '';
    // Date-only values should not shift due to timezone, so we force a local midnight.
    const d = new Date(`${yyyyMmDd}T00:00:00`);
    if (Number.isNaN(d.getTime())) return String(yyyyMmDd);
    return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    }).format(d);
};

const formatDisplayTime = (dateLike) => {
    if (!dateLike) return '';
    const d = new Date(dateLike);
    if (Number.isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    }).format(d);
};

const formatDisplayDateTime = (dateLike) => {
    if (!dateLike) return '';
    const d = new Date(dateLike);
    if (Number.isNaN(d.getTime())) return String(dateLike);
    const datePart = new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    }).format(d);
    const timePart = new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    }).format(d);
    return `${datePart} • ${timePart}`;
};

const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** WebAPI returns OPD rows; property is AppoimentNo → JSON camelCase appoimentNo (not appointmentNo). */
const extractAppointmentNoFromBookingRow = (row) => {
    const x = row && typeof row === 'object' ? row : {};
    const nested = x.opd && typeof x.opd === 'object' ? x.opd : null;
    const raw =
        (nested &&
            (nested.appoimentNo ??
                nested.AppoimentNo ??
                nested.appointmentNo ??
                nested.appoinmentNo)) ??
        x.appoimentNo ??
        x.AppoimentNo ??
        x.appointmentNo ??
        x.appoinmentNo ??
        x.appointmentNumber ??
        x.appoinmentNumber ??
        x.appNo ??
        x.no ??
        x.number;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
};

const parseAppointmentNoFromBookingResponse = (textBody, fallbackAppNo) => {
    const fallback = Number.isFinite(fallbackAppNo) && fallbackAppNo > 0 ? fallbackAppNo : null;
    if (!textBody) return fallback;
    const trimmed = String(textBody).trim();
    if (!trimmed) return fallback;
    try {
        const data = JSON.parse(trimmed);
        if (typeof data === 'number' && data > 0) return data;
        if (typeof data === 'string') {
            const n = Number(data);
            if (Number.isFinite(n) && n > 0) return n;
        }
        if (Array.isArray(data)) {
            for (const row of data) {
                const n = extractAppointmentNoFromBookingRow(row);
                if (n) return n;
            }
        } else if (data && typeof data === 'object') {
            const direct = extractAppointmentNoFromBookingRow(data);
            if (direct) return direct;
            for (const key of ['data', 'result', 'opd', 'OPD', 'value']) {
                const v = data[key];
                if (v != null && typeof v === 'object') {
                    const n = extractAppointmentNoFromBookingRow(v);
                    if (n) return n;
                }
                if (v != null && (typeof v === 'number' || typeof v === 'string')) {
                    const n = Number(v);
                    if (Number.isFinite(n) && n > 0) return n;
                }
            }
        }
    } catch {
        const n = Number(trimmed);
        if (Number.isFinite(n) && n > 0) return n;
    }
    return fallback;
};

const ChannelDoctorPage = () => {
    const today = useMemo(() => new Date(), []);

    const [date, setDate] = useState(formatDateInput(today));
    const [dateModalOpen, setDateModalOpen] = useState(false);
    const [dateDraft, setDateDraft] = useState(formatDateInput(today));
    const [calendarViewDate, setCalendarViewDate] = useState(
        () => new Date(today.getFullYear(), today.getMonth(), 1),
    );
    const [consultants, setConsultants] = useState([]);
    const [consultantsLoading, setConsultantsLoading] = useState(false);
    const [consultantsError, setConsultantsError] = useState('');
    const [consultantSearch, setConsultantSearch] = useState('');
    const [consultantModalOpen, setConsultantModalOpen] = useState(false);

    const [selectedConsultantId, setSelectedConsultantId] = useState('');
    const [schedules, setSchedules] = useState([]);
    const [schedulesLoading, setSchedulesLoading] = useState(false);
    const [schedulesError, setSchedulesError] = useState('');
    const [scheduleSearch, setScheduleSearch] = useState('');
    const [scheduleModalOpen, setScheduleModalOpen] = useState(false);

    const [selectedScheduleId, setSelectedScheduleId] = useState('');
    const [bookingsLoading, setBookingsLoading] = useState(false);
    const [bookingsError, setBookingsError] = useState('');
    const [bookedAppointmentNos, setBookedAppointmentNos] = useState([]);
    /** Distinct booked slot count per schedule id (same logic as Step 4); schedule list API often omits `booked`). */
    const [scheduleBookedSlotCounts, setScheduleBookedSlotCounts] = useState({});
    const [scheduleBookedCountsLoading, setScheduleBookedCountsLoading] = useState(false);

    const [patient, setPatient] = useState({
        fullName: '',
        age: '',
        months: 0,
        days: 0,
        sex: '1',
        mobileNumber: '',
    });

    const [appointmentNo, setAppointmentNo] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [submitSuccess, setSubmitSuccess] = useState('');
    const [bookingSuccessModalOpen, setBookingSuccessModalOpen] = useState(false);
    const [bookingSuccessAppointmentNo, setBookingSuccessAppointmentNo] = useState(null);

    const minSelectableDate = useMemo(() => formatDateInput(today), [today]);

    const calendarMonthLabel = useMemo(
        () =>
            new Intl.DateTimeFormat('en-GB', {
                month: 'long',
                year: 'numeric',
            }).format(calendarViewDate),
        [calendarViewDate],
    );

    const calendarDays = useMemo(() => {
        const year = calendarViewDate.getFullYear();
        const month = calendarViewDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const offset = firstDay.getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const days = [];

        for (let i = 0; i < offset; i += 1) {
            days.push(null);
        }

        for (let d = 1; d <= daysInMonth; d += 1) {
            const cellDate = new Date(year, month, d);
            const value = formatDateInput(cellDate);
            days.push({
                key: value,
                value,
                label: d,
                isDisabled: value < minSelectableDate,
                isSelected: value === dateDraft,
                isToday: value === minSelectableDate,
            });
        }

        return days;
    }, [calendarViewDate, dateDraft, minSelectableDate]);

    useEffect(() => {
        if (!bookingSuccessModalOpen) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [bookingSuccessModalOpen]);

    useEffect(() => {
        if (!dateModalOpen) return;
        const selected = new Date(`${date}T00:00:00`);
        const base = Number.isNaN(selected.getTime()) ? today : selected;
        setDateDraft(formatDateInput(base));
        setCalendarViewDate(new Date(base.getFullYear(), base.getMonth(), 1));
    }, [dateModalOpen, date, today]);

    // Load consultants whenever the date changes
    useEffect(() => {
        const controller = new AbortController();

        const loadConsultants = async () => {
            if (!date) {
                return;
            }

            setConsultants([]);
            setSelectedConsultantId('');
            setConsultantSearch('');
            setConsultantModalOpen(false);
            setSchedules([]);
            setSelectedScheduleId('');
            setScheduleSearch('');
            setScheduleModalOpen(false);
            setConsultantsError('');

            const url = `${API_BASE_URL}Channeling/GetAllConsultantByScheduleDate?StartFrom=${encodeURIComponent(
                date,
            )}`;

            try {
                setConsultantsLoading(true);
                const response = await fetch(url, {
                    signal: controller.signal,
                });

                if (!response.ok) {
                    throw new Error(`Unable to load consultants (HTTP ${response.status})`);
                }

                const data = await response.json();
                setConsultants(Array.isArray(data) ? data : []);
            } catch (error) {
                if (error.name === 'AbortError') {
                    return;
                }
                setConsultantsError(
                    formatFetchError(error, url) || 'Unable to load consultants for the selected date.',
                );
            } finally {
                setConsultantsLoading(false);
            }
        };

        loadConsultants();

        return () => controller.abort();
    }, [date]);

    // Load schedules when a consultant is chosen
    useEffect(() => {
        const controller = new AbortController();

        const loadSchedules = async () => {
            if (!selectedConsultantId) {
                setSchedules([]);
                setSelectedScheduleId('');
                setSchedulesError('');
                setBookingsLoading(false);
                setBookingsError('');
                setBookedAppointmentNos([]);
                setScheduleBookedSlotCounts({});
                setScheduleBookedCountsLoading(false);
                setScheduleSearch('');
                setScheduleModalOpen(false);
                return;
            }

            const status = 1; // ACTIVE sessions
            const url = `${API_BASE_URL}ChannelingShedule/GetAllSheduleGetByConsultantIdAndSessionStatus?Id=${encodeURIComponent(
                selectedConsultantId,
            )}&channellingScheduleStatus=${status}`;

            try {
                setSchedulesLoading(true);
                setSchedulesError('');
                setSchedules([]);
                setSelectedScheduleId('');
                setBookingsLoading(false);
                setBookingsError('');
                setBookedAppointmentNos([]);
                setScheduleBookedSlotCounts({});
                setScheduleBookedCountsLoading(false);
                setScheduleSearch('');
                setScheduleModalOpen(false);

                const response = await fetch(url, {
                    signal: controller.signal,
                });

                if (!response.ok) {
                    throw new Error(`Unable to load schedules (HTTP ${response.status})`);
                }

                const data = await response.json();
                const list = Array.isArray(data) ? data : [];
                setSchedules(list);

                setScheduleBookedSlotCounts({});
                if (list.length === 0) {
                    setScheduleBookedCountsLoading(false);
                } else {
                    setScheduleBookedCountsLoading(true);
                    try {
                        const entries = await Promise.all(
                            list.map(async (sch) => {
                                const sid = sch.id ?? sch.Id;
                                const bookUrl = `${API_BASE_URL}Channeling/ChannelingGetBySheduleId?Id=${encodeURIComponent(
                                    sid,
                                )}`;
                                const bookRes = await fetch(bookUrl, { signal: controller.signal });
                                if (!bookRes.ok) {
                                    return [String(sid), null];
                                }
                                const rows = await bookRes.json();
                                const arr = Array.isArray(rows) ? rows : [];
                                const nums = arr
                                    .map((x) => extractAppointmentNoFromBookingRow(x))
                                    .filter((n) => n !== null);
                                return [String(sid), new Set(nums).size];
                            }),
                        );
                        if (!controller.signal.aborted) {
                            const map = {};
                            entries.forEach(([sid, count]) => {
                                if (count != null) map[sid] = count;
                            });
                            setScheduleBookedSlotCounts(map);
                        }
                    } catch (bookErr) {
                        if (bookErr.name !== 'AbortError') {
                            /* keep counts empty; subtitle falls back to API fields */
                        }
                    } finally {
                        if (!controller.signal.aborted) {
                            setScheduleBookedCountsLoading(false);
                        }
                    }
                }
            } catch (error) {
                if (error.name === 'AbortError') {
                    return;
                }
                setSchedulesError(
                    formatFetchError(error, url) || 'Unable to load schedules for the selected consultant.',
                );
            } finally {
                setSchedulesLoading(false);
            }
        };

        loadSchedules();

        return () => controller.abort();
    }, [selectedConsultantId]);

    // Load booked appointment numbers for selected schedule (to show availability)
    useEffect(() => {
        const controller = new AbortController();

        const loadBookings = async () => {
            setBookingsError('');
            setBookedAppointmentNos([]);

            if (!selectedScheduleId) {
                setBookingsLoading(false);
                return;
            }

            const url = `${API_BASE_URL}Channeling/ChannelingGetBySheduleId?Id=${encodeURIComponent(
                selectedScheduleId,
            )}`;

            try {
                setBookingsLoading(true);
                const response = await fetch(url, { signal: controller.signal });
                if (!response.ok) {
                    throw new Error(`Unable to load bookings (HTTP ${response.status})`);
                }
                const data = await response.json();
                const list = Array.isArray(data) ? data : [];

                const nums = list
                    .map((x) => extractAppointmentNoFromBookingRow(x))
                    .filter((n) => n !== null);

                // unique + sorted
                const uniqueSorted = Array.from(new Set(nums)).sort((a, b) => a - b);
                setBookedAppointmentNos(uniqueSorted);
            } catch (error) {
                if (error.name === 'AbortError') return;
                setBookingsError(
                    formatFetchError(error, url) || 'Unable to load appointment availability.',
                );
            } finally {
                setBookingsLoading(false);
            }
        };

        loadBookings();

        return () => controller.abort();
    }, [selectedScheduleId]);

    const filteredConsultants = useMemo(
        () =>
            consultants.filter((c) => {
                if (!consultantSearch) return true;
                const term = consultantSearch.toLowerCase();
                const name = (c.name || '').toLowerCase();
                const spec = c.specialist && c.specialist.name ? c.specialist.name.toLowerCase() : '';
                return name.includes(term) || spec.includes(term);
            }),
        [consultants, consultantSearch],
    );

    const selectedConsultantLabel = useMemo(() => {
        if (!selectedConsultantId) return '';
        const found = consultants.find((c) => String(c.id) === String(selectedConsultantId));
        if (!found) return '';
        const spec = found.specialist && found.specialist.name ? ` – ${found.specialist.name}` : '';
        return `${found.name || ''}${spec}`;
    }, [consultants, selectedConsultantId]);

    const scheduleLabel = useCallback(
        (s) => {
            const rawDateTime = s.dateTime || s.scheduleDateTime || s.date || '';
            const room =
                s.room && (s.room.name || s.room.roomName)
                    ? `Room ${s.room.name || s.room.roomName}`
                    : '';
            const capacity = Number(s.noOfAppointment ?? s.NoOfAppointment ?? s.capacity ?? s.Capacity ?? 0);
            const sid = String(s.id ?? s.Id ?? '');
            const hasComputed =
                sid && Object.prototype.hasOwnProperty.call(scheduleBookedSlotCounts, sid);
            const bookedFromApi = Number(
                s.booked ?? s.bookedCount ?? s.Booked ?? s.allBookedAppoinment ?? s.allBookedAppointment ?? 0,
            );
            let bookedPart = '';
            if (capacity > 0) {
                if (hasComputed) {
                    bookedPart = `${scheduleBookedSlotCounts[sid]}/${capacity} booked`;
                } else if (scheduleBookedCountsLoading) {
                    bookedPart = `…/${capacity} booked`;
                } else {
                    bookedPart = `${Number.isFinite(bookedFromApi) ? bookedFromApi : 0}/${capacity} booked`;
                }
            }
            const capText = bookedPart;
            return {
                title: formatDisplayDateTime(rawDateTime),
                subtitle: [room, capText].filter(Boolean).join(' • '),
            };
        },
        [scheduleBookedSlotCounts, scheduleBookedCountsLoading],
    );

    const filteredSchedules = useMemo(() => {
        if (!scheduleSearch) return schedules;
        const term = scheduleSearch.toLowerCase();
        return schedules.filter((s) => {
            const { title, subtitle } = scheduleLabel(s);
            return (
                String(title).toLowerCase().includes(term) ||
                String(subtitle).toLowerCase().includes(term)
            );
        });
    }, [schedules, scheduleSearch, scheduleLabel]);

    const selectedSchedule = useMemo(
        () =>
            schedules.find((s) => String(s.id ?? s.Id) === String(selectedScheduleId)) || null,
        [schedules, selectedScheduleId],
    );

    const scheduleCapacity = useMemo(() => {
        if (!selectedSchedule) return 0;
        const cap = Number(selectedSchedule.noOfAppointment ?? selectedSchedule.capacity ?? 0);
        return Number.isFinite(cap) && cap > 0 ? cap : 0;
    }, [selectedSchedule]);

    const availableAppointmentNos = useMemo(() => {
        if (!scheduleCapacity) return [];
        const bookedSet = new Set(bookedAppointmentNos);
        const available = [];
        for (let i = 1; i <= scheduleCapacity; i += 1) {
            if (!bookedSet.has(i)) available.push(i);
        }
        return available;
    }, [bookedAppointmentNos, scheduleCapacity]);

    const appointmentSlotRows = useMemo(() => {
        if (!scheduleCapacity) return [];
        const bookedSet = new Set(bookedAppointmentNos);
        const rows = [];
        for (let n = 1; n <= scheduleCapacity; n += 1) {
            rows.push({ n, booked: bookedSet.has(n) });
        }
        return rows;
    }, [scheduleCapacity, bookedAppointmentNos]);

    const bookedSlotsCount = bookedAppointmentNos.length;
    const availableSlotsCount = availableAppointmentNos.length;
    const bookedPercentOfCapacity =
        scheduleCapacity > 0
            ? Math.min(100, Math.round((bookedSlotsCount / scheduleCapacity) * 100))
            : 0;

    const selectedScheduleLabel = useMemo(() => {
        if (!selectedScheduleId) return '';
        const found = schedules.find((s) => String(s.id ?? s.Id) === String(selectedScheduleId));
        if (!found) return '';
        const rawDateTime = found.dateTime || found.scheduleDateTime || found.date || '';
        const dateText = formatDisplayDate(rawDateTime ? String(rawDateTime).slice(0, 10) : '');
        const timeText = formatDisplayTime(rawDateTime);
        const { subtitle } = scheduleLabel(found);
        const main = [dateText, timeText].filter(Boolean).join(' • ');
        return subtitle ? `${main} – ${subtitle}` : main;
    }, [schedules, selectedScheduleId, scheduleLabel]);

    const handlePatientChange = (e) => {
        const { name, value } = e.target;
        setPatient((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError('');
        setSubmitSuccess('');

        if (!selectedConsultantId || !selectedScheduleId) {
            setSubmitError('Please select a doctor and a schedule before booking.');
            return;
        }

        if (!patient.fullName || !patient.age || !patient.mobileNumber) {
            setSubmitError('Please fill in patient name, age and mobile number.');
            return;
        }

        const appNo = appointmentNo ? Number(appointmentNo) : 0;

        // Build OPDDto payload expected by MVC endpoint Channeling/AddNewChannelAsync
        const schedule = selectedSchedule || null;
        const hospitalFee = Number(
            schedule?.hospitalFee ??
                schedule?.hospitalFees ??
                schedule?.hospitalFeeAmount ??
                schedule?.HospitalFee ??
                0,
        );
        const consultantFee = Number(
            schedule?.consultantFee ??
                schedule?.consultantFees ??
                schedule?.consultantFeeAmount ??
                schedule?.ConsultantFee ??
                0,
        );

        const body = {
            patient: {
                FullName: patient.fullName,
                Age: Number(patient.age) || 0,
                Months: 0,
                Days: 0,
                Sex: Number(patient.sex) || 1,
                MobileNumber: String(patient.mobileNumber || '').trim(),
            },
            opd: {
                schedularId: Number(selectedScheduleId),
                ConsultantID: Number(selectedConsultantId),
                AppoimentNo: appNo,
                HospitalFee: Number.isFinite(hospitalFee) ? hospitalFee : 0,
                ConsultantFee: Number.isFinite(consultantFee) ? consultantFee : 0,
            },
            isChanneling: 1,
            isVOGScan: 0,
            isCardioScan: 0,
            isExerciseBook: 0,
            isClinicBook: 0,
        };

        const bookingUrl =
            BOOKING_URL_PREFIX === ''
                ? `/${BOOKING_PATH}`
                : `${BOOKING_URL_PREFIX}/${BOOKING_PATH}`;
        let isSameOrigin = false;
        try {
            isSameOrigin =
                new URL(bookingUrl, window.location.href).origin === window.location.origin;
        } catch {
            isSameOrigin = false;
        }

        try {
            setSubmitting(true);
            const response = await fetch(bookingUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                // MVC endpoint relies on cookies/session in many deployments.
                // Browsers only allow cross-origin credentials if the server sets:
                // Access-Control-Allow-Origin to the exact origin (not '*') AND Access-Control-Allow-Credentials: true.
                // So we only include credentials when same-origin (recommended deployment setup).
                credentials: isSameOrigin ? 'include' : 'omit',
                body: JSON.stringify(body),
            });

            if (!response.ok) {
                const text = await response.text();
                if (!isSameOrigin) {
                    throw new Error(
                        `Booking failed due to cross-origin restrictions. Deploy this site under the same domain as the UI, or configure UI CORS to allow ${window.location.origin} with credentials. Server response: ${text || `HTTP ${response.status}`}`,
                    );
                }
                throw new Error(
                    text ||
                        `Booking failed (HTTP ${response.status}). Endpoint: ${bookingUrl}. ` +
                        `If Swagger shows a different path or /api/ prefix, set REACT_APP_BOOKING_BASE_URL or REACT_APP_BOOKING_PATH in .env.`,
                );
            }

            const textBody = await response.text();
            const bookedNo = parseAppointmentNoFromBookingResponse(textBody, appNo);
            setBookingSuccessAppointmentNo(bookedNo);
            setBookingSuccessModalOpen(true);
            setSubmitSuccess('');
            setAppointmentNo('');
            setPatient({
                fullName: '',
                age: '',
                months: 0,
                days: 0,
                sex: '1',
                mobileNumber: '',
            });
        } catch (error) {
            setSubmitError(
                formatFetchError(error, bookingUrl) || 'Unable to complete booking. Please try again.',
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Fragment>
            <style>
                {`
.channel-appointment-field select option.channel-appt-booked:disabled {
  color: #c0392b;
  background-color: #fde8e8;
  font-weight: 600;
}
.channel-slot-viz {
  margin-top: 12px;
  padding: 14px 16px;
  border-radius: 14px;
  background: linear-gradient(145deg, #f8fafc 0%, #eef2ff 55%, #fdf8f6 100%);
  border: 1px solid rgba(30, 64, 175, 0.1);
  box-shadow: 0 4px 18px rgba(15, 23, 42, 0.06);
}
.channel-slot-viz-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 12px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #475569;
  font-weight: 600;
}
.channel-slot-viz-label span:last-child {
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
  text-transform: none;
  letter-spacing: 0;
}
.channel-slot-viz-track {
  position: relative;
  height: 12px;
  border-radius: 999px;
  background: linear-gradient(90deg, #bbf7d0 0%, #99f6e4 50%, #a5f3fc 100%);
  overflow: hidden;
  margin-bottom: 12px;
  box-shadow: inset 0 1px 2px rgba(255, 255, 255, 0.65);
}
.channel-slot-viz-booked {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #f87171 0%, #fb923c 55%, #fbbf24 100%);
  transition: width 0.55s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 0 12px rgba(248, 113, 113, 0.35);
  animation: channel-slot-shimmer 2.2s ease-in-out infinite;
}
@keyframes channel-slot-shimmer {
  0%, 100% { filter: brightness(1); }
  50% { filter: brightness(1.08); }
}
.channel-slot-viz-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: stretch;
}
.channel-slot-chip {
  flex: 1 1 88px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 8px 6px;
  border-radius: 10px;
  font-size: 11px;
  line-height: 1.25;
  text-align: center;
  border: 1px solid transparent;
}
.channel-slot-chip i {
  font-size: 14px;
  margin-bottom: 2px;
  opacity: 0.9;
}
.channel-slot-chip strong {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.02em;
}
.channel-slot-chip--booked {
  background: rgba(254, 226, 226, 0.85);
  border-color: rgba(248, 113, 113, 0.35);
  color: #991b1b;
}
.channel-slot-chip--open {
  background: rgba(220, 252, 231, 0.9);
  border-color: rgba(34, 197, 94, 0.35);
  color: #14532d;
}
.channel-slot-chip--total {
  background: rgba(241, 245, 249, 0.95);
  border-color: rgba(100, 116, 139, 0.25);
  color: #334155;
}
.channel-slot-viz-foot {
  margin-top: 10px;
  font-size: 12px;
  color: #64748b;
  text-align: center;
  line-height: 1.45;
}
.channel-slot-viz-foot strong {
  color: #0f172a;
}
`}
            </style>
            <Navbar hclass={'wpo-site-header wpo-site-header-s2'} Logo={Logo} />
            <PageTitle
                pageTitle={'Channel a Doctor'}
                pagesub={'Channel a Doctor'}
                bgImage={ServiceBanner}
            />

            <section className="wpo-contact-pg-section section-padding channel-page">
                <div className="container">
                    <div className="wpo-contact-title channel-title">
                        <h2>Book Your Doctor in 4 Easy Steps</h2>
                        <p>Select a date, choose your doctor and complete a simple form to confirm your visit.</p>
                    </div>

                    <div className="row g-4 channel-steps-row">
                        <div className="col-lg-4 col-md-6 col-12">
                            <div className="wpo-contact-form-area channel-card">
                                <div className="channel-step-badge">Step 1</div>
                                <h3>Select Date</h3>
                                <p>Choose the date you wish to see available consultants.</p>
                                <button
                                    type="button"
                                    className="channel-date-picker"
                                    onClick={() => setDateModalOpen(true)}
                                >
                                    <div className="channel-date-icon">
                                        <i className="ti-calendar" />
                                    </div>
                                    <div className="channel-date-text">
                                        <span className="label">Selected date</span>
                                        <span className="value">{formatDisplayDate(date)}</span>
                                    </div>
                                </button>
                                {consultantsLoading && <p className="channel-muted">Loading consultants...</p>}
                                {consultantsError && (
                                    <p className="channel-error">{consultantsError}</p>
                                )}
                            </div>
                        </div>

                        <div className="col-lg-4 col-md-6 col-12">
                            <div className="wpo-contact-form-area channel-card">
                                <div className="channel-step-badge">Step 2</div>
                                <h3>Select Consultant</h3>
                                <p>Pick a doctor who is available on the selected date.</p>
                                <div className="form-field">
                                    <button
                                        type="button"
                                        className="channel-modal-trigger"
                                        onClick={() => {
                                            if (!consultants.length) return;
                                            setConsultantModalOpen(true);
                                        }}
                                        disabled={consultantsLoading || !!consultantsError}
                                    >
                                        <span className="icon">
                                            <i className="ti-user" />
                                        </span>
                                        <div className="text">
                                            {!selectedConsultantLabel && (
                                                <span className="label">Consultant</span>
                                            )}
                                            <span className="value">
                                                {selectedConsultantLabel || 'choose your doctor'}
                                            </span>
                                        </div>
                                    </button>
                                </div>
                                {schedulesLoading && selectedConsultantId && (
                                    <p className="channel-muted">Loading schedules...</p>
                                )}
                                {schedulesError && (
                                    <p className="channel-error">{schedulesError}</p>
                                )}
                            </div>
                        </div>

                        <div className="col-lg-4 col-md-12 col-12">
                            <div className="wpo-contact-form-area channel-card">
                                <div className="channel-step-badge">Step 3</div>
                                <h3>Select Time / Session</h3>
                                <p>Choose a time slot from the selected doctor&apos;s active sessions.</p>
                                <div className="form-field">
                                    <button
                                        type="button"
                                        className="channel-modal-trigger"
                                        onClick={() => {
                                            if (!schedules.length) return;
                                            setScheduleModalOpen(true);
                                        }}
                                        disabled={schedulesLoading || !selectedConsultantId || !!schedulesError}
                                    >
                                        <span className="icon">
                                            <i className="ti-time" />
                                        </span>
                                        <div className="text">
                                            {!selectedScheduleLabel && (
                                                <span className="label">Session</span>
                                            )}
                                            <span className="value">
                                                {selectedScheduleLabel || 'choose time / session'}
                                            </span>
                                        </div>
                                    </button>
                                </div>
                                {!schedules.length && selectedConsultantId && !schedulesLoading && (
                                    <p className="channel-muted">No active sessions for this consultant on the selected date.</p>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="row align-items-stretch channel-patient-row">
                        <div className="col-lg-8 col-md-12 col-12">
                            <div className="wpo-contact-form-area channel-card channel-main-form">
                                <div className="channel-step-badge">Step 4</div>
                                <h3>Patient Details</h3>
                                <p className="channel-subtitle">Please enter your basic details to confirm the booking.</p>
                                <form onSubmit={handleSubmit}>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="form-field">
                                                <input
                                                    type="text"
                                                    name="fullName"
                                                    value={patient.fullName}
                                                    onChange={handlePatientChange}
                                                    placeholder="Full name *"
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="form-field">
                                                <input
                                                    type="number"
                                                    name="age"
                                                    value={patient.age}
                                                    onChange={handlePatientChange}
                                                    placeholder="Age *"
                                                    min="0"
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="form-field">
                                                <select
                                                    name="sex"
                                                    value={patient.sex}
                                                    onChange={handlePatientChange}
                                                >
                                                    <option value="1">Male</option>
                                                    <option value="2">Female</option>
                                                    <option value="3">Other</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="form-field">
                                                <input
                                                    type="tel"
                                                    name="mobileNumber"
                                                    value={patient.mobileNumber}
                                                    onChange={handlePatientChange}
                                                    placeholder="Mobile number *"
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-field channel-appointment-field">
                                                <select
                                                    name="appointmentNo"
                                                    value={appointmentNo}
                                                    onChange={(e) => setAppointmentNo(e.target.value)}
                                                    disabled={!selectedScheduleId || bookingsLoading}
                                                >
                                                    <option value="">
                                                        {selectedScheduleId
                                                            ? bookingsLoading
                                                                ? 'Loading available numbers...'
                                                                : 'Auto assign'
                                                            : 'Select a session first'}
                                                    </option>
                                                    {appointmentSlotRows.map(({ n, booked }) => (
                                                        <option
                                                            key={n}
                                                            value={String(n)}
                                                            disabled={booked}
                                                            className={booked ? 'channel-appt-booked' : undefined}
                                                            style={
                                                                booked
                                                                    ? { color: '#c0392b', fontWeight: 600 }
                                                                    : undefined
                                                            }
                                                        >
                                                            {booked
                                                                ? `Appointment - ${n} (Booked)`
                                                                : `Appointment - ${n}`}
                                                        </option>
                                                    ))}
                                                </select>
                                                {bookingsError && (
                                                    <div className="channel-muted" style={{ color: '#e74c3c' }}>
                                                        {bookingsError}
                                                    </div>
                                                )}
                                                {!!selectedScheduleId && !bookingsLoading && scheduleCapacity > 0 && (
                                                    <div
                                                        className="channel-slot-viz"
                                                        role="status"
                                                        aria-label={`Session ${bookedPercentOfCapacity} percent full. ${bookedSlotsCount} taken, ${availableSlotsCount} open, ${scheduleCapacity} total slots.`}
                                                    >
                                                        <div className="channel-slot-viz-label">
                                                            <span>Session pulse</span>
                                                            <span>
                                                                {bookedPercentOfCapacity}% full · {availableSlotsCount}{' '}
                                                                seat{availableSlotsCount === 1 ? '' : 's'} open
                                                            </span>
                                                        </div>
                                                        <div className="channel-slot-viz-track">
                                                            <div
                                                                className="channel-slot-viz-booked"
                                                                style={{ width: `${bookedPercentOfCapacity}%` }}
                                                            />
                                                        </div>
                                                        <div className="channel-slot-viz-stats">
                                                            <div className="channel-slot-chip channel-slot-chip--booked">
                                                                <i className="ti-minus" aria-hidden />
                                                                <span>Taken</span>
                                                                <strong>{bookedSlotsCount}</strong>
                                                            </div>
                                                            <div className="channel-slot-chip channel-slot-chip--open">
                                                                <i className="ti-ticket" aria-hidden />
                                                                <span>Open</span>
                                                                <strong>{availableSlotsCount}</strong>
                                                            </div>
                                                            <div className="channel-slot-chip channel-slot-chip--total">
                                                                <i className="ti-layout" aria-hidden />
                                                                <span>Capacity</span>
                                                                <strong>{scheduleCapacity}</strong>
                                                            </div>
                                                        </div>
                                                        <div className="channel-slot-viz-foot">
                                                            <strong>{availableSlotsCount}</strong> of{' '}
                                                            <strong>{scheduleCapacity}</strong> appointment numbers are
                                                            still free for this session.
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {submitError && (
                                        <p className="channel-error mt-2">{submitError}</p>
                                    )}
                                    {submitSuccess && (
                                        <p className="channel-success mt-2">{submitSuccess}</p>
                                    )}

                                    <div className="submit-area">
                                        <button
                                            type="submit"
                                            className="theme-btn"
                                            disabled={submitting}
                                        >
                                            {submitting ? 'Booking...' : 'Confirm Booking'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>

                        <div className="col-lg-4 col-md-12 col-12">
                            <div className="wpo-contact-form-area channel-card channel-info-card">
                                <h3>Before You Arrive</h3>
                                <ul className="channel-info-list">
                                    <li>
                                        <span className="bullet" />
                                        Bookings are subject to availability and hospital confirmation.
                                    </li>
                                    <li>
                                        <span className="bullet" />
                                        Please arrive at the hospital at least 30 minutes before your appointment time.
                                    </li>
                                    <li>
                                        <span className="bullet" />
                                        Bring your previous medical reports and current medications, if any.
                                    </li>
                                    <li>
                                        <span className="bullet" />
                                        If you need to change or cancel your booking, contact the hospital helpdesk.
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {dateModalOpen && (
                        <div className="channel-modal-overlay">
                            <div
                                className="channel-modal channel-date-modal"
                                onClick={(e) => {
                                    e.stopPropagation();
                                }}
                            >
                                <div className="channel-modal-body channel-date-modal-body">
                                    <div className="channel-date-calendar">
                                        <div className="channel-date-calendar-head">
                                            <button
                                                type="button"
                                                className="channel-date-nav"
                                                onClick={() =>
                                                    setCalendarViewDate(
                                                        new Date(
                                                            calendarViewDate.getFullYear(),
                                                            calendarViewDate.getMonth() - 1,
                                                            1,
                                                        ),
                                                    )
                                                }
                                                aria-label="Previous month"
                                            >
                                                <i className="ti-angle-left" />
                                            </button>
                                            <strong>{calendarMonthLabel}</strong>
                                            <button
                                                type="button"
                                                className="channel-date-nav"
                                                onClick={() =>
                                                    setCalendarViewDate(
                                                        new Date(
                                                            calendarViewDate.getFullYear(),
                                                            calendarViewDate.getMonth() + 1,
                                                            1,
                                                        ),
                                                    )
                                                }
                                                aria-label="Next month"
                                            >
                                                <i className="ti-angle-right" />
                                            </button>
                                        </div>

                                        <div className="channel-date-weekdays">
                                            {WEEK_DAYS.map((weekDay) => (
                                                <span key={weekDay}>{weekDay}</span>
                                            ))}
                                        </div>

                                        <div className="channel-date-days-grid">
                                            {calendarDays.map((day, idx) => {
                                                if (!day) {
                                                    return <span key={`blank-${idx}`} className="channel-date-day-empty" />;
                                                }

                                                return (
                                                    <button
                                                        key={day.key}
                                                        type="button"
                                                        className={`channel-date-day${
                                                            day.isSelected ? ' is-selected' : ''
                                                        }${day.isToday ? ' is-today' : ''}`}
                                                        onClick={() => {
                                                            if (day.isDisabled) return;
                                                            setDateDraft(day.value);
                                                        }}
                                                        disabled={day.isDisabled}
                                                        aria-label={formatDisplayDate(day.value)}
                                                    >
                                                        {day.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className="channel-date-popup-actions">
                                        <button
                                            type="button"
                                            className="channel-date-popup-btn channel-date-popup-btn--ghost"
                                            onClick={() => setDateModalOpen(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="channel-date-popup-btn channel-date-popup-btn--primary"
                                            onClick={() => {
                                                setDate(dateDraft);
                                                setDateModalOpen(false);
                                            }}
                                        >
                                            Apply Date
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {consultantModalOpen && (
                        <div className="channel-modal-overlay" onClick={() => setConsultantModalOpen(false)}>
                            <div
                                className="channel-modal"
                                onClick={(e) => {
                                    e.stopPropagation();
                                }}
                            >
                                <div className="channel-modal-header">
                                    <h4>Select Consultant</h4>
                                    <button
                                        type="button"
                                        className="channel-modal-close"
                                        onClick={() => setConsultantModalOpen(false)}
                                    >
                                        <i className="ti-close" />
                                    </button>
                                </div>
                                <div className="channel-modal-search">
                                    <i className="ti-search" />
                                    <input
                                        type="text"
                                        placeholder="Search by doctor name or specialty"
                                        value={consultantSearch}
                                        onChange={(e) => setConsultantSearch(e.target.value)}
                                    />
                                </div>
                                <div className="channel-modal-body">
                                    {consultantsLoading && (
                                        <div className="channel-modal-empty">Loading consultants...</div>
                                    )}
                                    {consultantsError && !consultantsLoading && (
                                        <div className="channel-modal-empty channel-modal-error">
                                            {consultantsError}
                                        </div>
                                    )}
                                    {!consultantsLoading && !consultantsError && filteredConsultants.length === 0 && (
                                        <div className="channel-modal-empty">
                                            No consultants match your search.
                                        </div>
                                    )}
                                    <div className="channel-modal-list">
                                        {filteredConsultants.map((c) => {
                                            const spec =
                                                c.specialist && c.specialist.name
                                                    ? c.specialist.name
                                                    : '';
                                            return (
                                                <button
                                                    type="button"
                                                    key={c.id}
                                                    className="channel-modal-item"
                                                    onClick={() => {
                                                        setSelectedConsultantId(c.id);
                                                        setConsultantModalOpen(false);
                                                    }}
                                                >
                                                    <div className="avatar">
                                                        <span>{(c.name || '?').charAt(0)}</span>
                                                    </div>
                                                    <div className="info">
                                                        <span className="name">{c.name}</span>
                                                        {spec && <span className="spec">{spec}</span>}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {scheduleModalOpen && (
                        <div className="channel-modal-overlay" onClick={() => setScheduleModalOpen(false)}>
                            <div
                                className="channel-modal"
                                onClick={(e) => {
                                    e.stopPropagation();
                                }}
                            >
                                <div className="channel-modal-header">
                                    <h4>Select Session</h4>
                                    <button
                                        type="button"
                                        className="channel-modal-close"
                                        onClick={() => setScheduleModalOpen(false)}
                                    >
                                        <i className="ti-close" />
                                    </button>
                                </div>
                                <div className="channel-modal-search">
                                    <i className="ti-search" />
                                    <input
                                        type="text"
                                        placeholder="Search by time or room"
                                        value={scheduleSearch}
                                        onChange={(e) => setScheduleSearch(e.target.value)}
                                    />
                                </div>
                                <div className="channel-modal-body">
                                    {schedulesLoading && (
                                        <div className="channel-modal-empty">Loading sessions...</div>
                                    )}
                                    {schedulesError && !schedulesLoading && (
                                        <div className="channel-modal-empty channel-modal-error">
                                            {schedulesError}
                                        </div>
                                    )}
                                    {!schedulesLoading && !schedulesError && filteredSchedules.length === 0 && (
                                        <div className="channel-modal-empty">
                                            No sessions match your search.
                                        </div>
                                    )}
                                    <div className="channel-modal-list">
                                        {filteredSchedules.map((s) => {
                                            const { title, subtitle } = scheduleLabel(s);
                                            const sid = s.id ?? s.Id;
                                            return (
                                                <button
                                                    type="button"
                                                    key={sid}
                                                    className="channel-modal-item"
                                                    onClick={() => {
                                                        setSelectedScheduleId(sid);
                                                        setScheduleModalOpen(false);
                                                    }}
                                                >
                                                    <div className="avatar">
                                                        <i className="ti-time" />
                                                    </div>
                                                    <div className="info">
                                                        <span className="name">{title}</span>
                                                        {subtitle && <span className="spec">{subtitle}</span>}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {bookingSuccessModalOpen && (
                        <div
                            className="channel-booking-success-overlay"
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="channel-booking-success-title"
                        >
                            <div
                                className="channel-booking-success-modal"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="channel-booking-success-badge" aria-hidden>
                                    <i className="ti-check" />
                                </div>
                                <h3 id="channel-booking-success-title">{"You're all set"}</h3>
                                <p className="channel-booking-success-lead">
                                    Your channel appointment is confirmed. Please save your appointment number for
                                    your visit.
                                </p>
                                <div className="channel-booking-success-number-wrap">
                                    <span className="channel-booking-success-label">Appointment number</span>
                                    <div className="channel-booking-success-number">
                                        {bookingSuccessAppointmentNo != null
                                            ? bookingSuccessAppointmentNo
                                            : '—'}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="theme-btn channel-booking-success-ok"
                                    onClick={() => {
                                        window.location.assign('/');
                                    }}
                                >
                                    OK
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            <Footer hclass={'wpo-site-footer'} />
            <Scrollbar />
        </Fragment>
    );
};

export default ChannelDoctorPage;

